"use client";

import React, { useMemo, useState } from "react";
import SeasonalImage from "@/components/SeasonalImage";
import { X } from "lucide-react";
import { useExamScore } from "@/components/ExamScoreContext";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import {
  MobileBottomToolRegistration,
  useMobileBottomTools,
} from "@/components/mobile-bottom-tools";
import type { MobileBottomToolItem } from "@/components/mobile-bottom-tools";
import { cn } from "@/lib/utils";

/**
 * 정답률에 따라 감자 캐릭터 이미지 선택 (모바일 dock + 데스크탑 점수 패널 공용, JCG-2066/JCG-2221).
 * 문제를 한 문제도 안 풀었을 때(totalAttempted===0)는 기본 감자.
 * 기본 이미지 경로만 돌려주고, 시즌 이미지는 렌더하는 `SeasonalImage` 가 교체한다(JCG-3694).
 */
export function getScoreGamjaImage(
  percentage: number,
  totalAttempted: number,
): string {
  if (totalAttempted === 0) return "/exams/3d_gamja.webp";
  if (percentage <= 28.6) return "/exams/3d_sprout_gamja.webp";
  if (percentage <= 44.4) return "/exams/3d_dirt_gamja.webp";
  if (percentage <= 57.1) return "/exams/3d_gamja.webp";
  if (percentage <= 75.0) return "/exams/3d_raining-happy_gamja.webp";
  return "/exams/3d_beach-sprout_gamja.webp";
}

export function getExamScorePercentage(
  correctCount: number,
  totalAttempted: number,
): number {
  return totalAttempted === 0
    ? 0
    : Math.floor((correctCount / totalAttempted) * 100);
}

export function formatExamScoreValue(value: number): string {
  return new Intl.NumberFormat("ko-KR", {
    maximumFractionDigits: 2,
  }).format(value);
}

interface ExamScoreSummaryPanelProps {
  correctCount: number;
  totalAttempted: number;
  percentage: number;
  currentImage: string;
  scoreTitle?: string;
  className?: string;
  imageClassName?: string;
}

export function ExamScoreSummaryPanel({
  correctCount,
  totalAttempted,
  percentage,
  currentImage,
  scoreTitle,
  className,
  imageClassName,
}: ExamScoreSummaryPanelProps) {
  return (
    <div
      className={cn("flex flex-col items-center gap-2 text-center", className)}
      aria-live="polite"
    >
      {scoreTitle ? (
        <h3 className="m-0 mb-1 w-full max-w-[18rem] rounded-xl border border-amber-300 bg-amber-50/80 px-3 py-2.5 text-pretty break-words text-center text-sm font-extrabold leading-snug text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          {scoreTitle}
        </h3>
      ) : null}
      <SeasonalImage
        src={currentImage}
        alt=""
        aria-hidden
        width={96}
        height={96}
        className={cn(
          "h-20 w-20 rounded-full border border-amber-300 shadow-sm dark:border-amber-500/50",
          imageClassName,
        )}
      />
      {totalAttempted === 0 ? (
        <p className="text-sm leading-relaxed text-muted-foreground">
          아직 푼 문제가 없어요.
          <br />
          문제를 풀면 정답률이 표시돼요!
        </p>
      ) : (
        <>
          <div className="text-xs font-bold text-muted-foreground">
            현재 정답률
          </div>
          <div className="text-4xl font-extrabold leading-none tabular-nums text-amber-500 dark:text-amber-400">
            {percentage}%
          </div>
          <div className="text-sm tabular-nums text-muted-foreground">
            {totalAttempted}문제 중 {formatExamScoreValue(correctCount)}문제 정답
          </div>
        </>
      )}
    </div>
  );
}

interface ExamScoreMobileToolProps {
  correctCount: number;
  totalAttempted: number;
  percentage: number;
  currentImage: string;
  scoreTitle?: string;
}

function ExamScoreMobileTool({
  correctCount,
  totalAttempted,
  percentage,
  currentImage,
  scoreTitle,
}: ExamScoreMobileToolProps) {
  const { pageTitle } = useMobileBottomTools();
  const displayTitle = scoreTitle ?? pageTitle;
  const examAriaPrefix = displayTitle ? `${displayTitle}, ` : "";
  const answerStatus =
    totalAttempted === 0 ? "미풀이" : `${formatExamScoreValue(correctCount)}/${totalAttempted} 정답`;
  const scoreAriaDetail =
    totalAttempted === 0
      ? "아직 푼 문제가 없습니다"
      : `현재 정답률 ${percentage}%, ${totalAttempted}문제 중 ${formatExamScoreValue(correctCount)}문제 정답`;
  const scoreAriaLabel = `${examAriaPrefix}점수 패널 열기, ${scoreAriaDetail}`;

  const tool = useMemo<MobileBottomToolItem>(
    () => ({
      id: "score",
      kind: "status-panel" as const,
      label: "점수",
      hideLabel: true,
      order: 15,
      ariaLabel: scoreAriaLabel,
      status: answerStatus,
      renderIcon: ({ className }) => (
        <span className="flex h-6 shrink-0 items-center gap-1.5" aria-hidden>
          <SeasonalImage
            src={currentImage}
            alt=""
            width={24}
            height={24}
            className={cn(
              className,
              "h-6 w-6 rounded-full border border-amber-500 shadow-sm dark:border-amber-400",
            )}
          />
          <span className="text-xs font-extrabold leading-none tabular-nums text-amber-600 dark:text-amber-300">
            {percentage}%
          </span>
        </span>
      ),
      sheet: {
        title: "점수",
        attached: true,
        content: (
          <ExamScoreSummaryPanel
            correctCount={correctCount}
            totalAttempted={totalAttempted}
            percentage={percentage}
            currentImage={currentImage}
            scoreTitle={displayTitle}
            className="py-2"
            imageClassName="h-24 w-24 shadow-lg"
          />
        ),
      },
    }),
    [
      answerStatus,
      correctCount,
      currentImage,
      displayTitle,
      percentage,
      scoreAriaLabel,
      totalAttempted,
    ],
  );

  return <MobileBottomToolRegistration tool={tool} />;
}

interface ExamScoreModalProps {
  scoreTitle?: string;
}

const ExamScoreModal: React.FC<ExamScoreModalProps> = ({ scoreTitle }) => {
  const examScore = useExamScore();
  const { inShell } = useArticleShellSlots();
  const [isMinimized, setIsMinimized] = useState(false);

  // Context가 없거나 데스크탑 셸이면 렌더링하지 않음.
  // 셸에서는 ArticleScorePanel 이 좌측 점수 Section 을 담당한다.
  if (!examScore || inShell) return null;

  const { correctCount, totalAttempted } = examScore;
  const percentage = getExamScorePercentage(correctCount, totalAttempted);
  const currentImage = getScoreGamjaImage(percentage, totalAttempted);

  const desktopFloating = isMinimized ? (
    <div className="fixed bottom-12 right-20 z-50 hidden lg:block">
      <button
        type="button"
        onClick={() => setIsMinimized(false)}
        className="group relative cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        aria-label="정답률 보기"
      >
        <SeasonalImage
          src={currentImage}
          alt=""
          aria-hidden
          width={48}
          height={48}
          className="h-12 w-12 rounded-full border border-amber-500 shadow-lg transition-transform group-hover:scale-110 dark:border-amber-400"
        />
        <span className="absolute -left-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-xs font-bold tabular-nums text-white dark:bg-amber-400 dark:text-amber-950">
          {percentage}%
        </span>
      </button>
    </div>
  ) : (
    <div className="fixed bottom-12 right-20 z-50 hidden lg:block">
      <div className="relative rounded-lg border-2 border-amber-500 bg-card p-4 text-card-foreground shadow-2xl dark:border-amber-400">
        <button
          type="button"
          onClick={() => setIsMinimized(true)}
          className="absolute -right-2 -top-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          aria-label="정답률 최소화"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
        <ExamScoreSummaryPanel
          correctCount={correctCount}
          totalAttempted={totalAttempted}
          percentage={percentage}
          currentImage={currentImage}
          className="min-w-[180px]"
          imageClassName="h-[100px] w-[100px] shadow-2xl"
        />
      </div>
    </div>
  );

  return (
    <>
      <ExamScoreMobileTool
        correctCount={correctCount}
        totalAttempted={totalAttempted}
        percentage={percentage}
        currentImage={currentImage}
        scoreTitle={scoreTitle}
      />
      {desktopFloating}
    </>
  );
};

export default ExamScoreModal;
